// src/services/campaignApi.js
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api/baseQuery";
import { HTTP_STATUS } from "../api/constants";

// Type definitions
export const DiscountType = {
  Percentage: 0,
  FixedAmount: 1,
  GiftCard: 2,
  PromotionalProduct: 3,
};

// API Response types
export const ResponseStatus = {
  OK: 0,
  CREATED: 1,
  ERROR: 2,
  FORBIDDEN: 3,
  UNAUTHORIZED: 4,
  INVALID: 5,
  NOT_FOUND: 6,
  NO_CONTENT: 7,
  CONFLICT: 8,
  CRITICAL_ERROR: 9,
  UNAVAILABLE: 10,
};

// Using centralized baseQueryWithReauth from api/baseQuery.js
export const campaignApi = createApi({
  reducerPath: "campaignApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Campaign", "CouponCodes", "Category", "Brand"],
  endpoints: (builder) => ({
    // Get campaign list with pagination
    getCampaigns: builder.query({
      query: (params = {}) => {
        // Clean and prepare query parameters
        const queryParams = {
          page: typeof params === 'number' ? params : (params.page || 0),
          pageSize: params.pageSize || 8,
          ...(params.keyword && params.keyword.trim() && { keyword: params.keyword.trim() }),
          ...(params.status !== undefined && { status: params.status }),
          ...(params.categoryId && { categoryId: params.categoryId }),
          ...(params.brandId && { brandId: params.brandId }),
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
          // Include sort parameters only when provided (component handles defaults)
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder })
        };

        console.log('🔍 Admin Campaign API Query Params (keyword):', queryParams);

        return {
          url: "/campaign/list",
          method: "GET",
          params: queryParams
        };
      },
      transformResponse: (response) => {
        if (!response) {
          return {
            value: {
              campaigns: [],
              totalCount: 0,
            },
            isSuccess: false,
            errors: [],
            validationErrors: [],
          };
        }

        return {
          value: {
            campaigns: response.value.campaigns || [],
            totalCount: response.value.totalCount || 0,
          },
          isSuccess: response.isSuccess,
          errors: response.errors || [],
          validationErrors: response.validationErrors || [],
        };
      },
      providesTags: (result) =>
        result?.value?.campaigns
          ? [
              ...result.value.campaigns.map(({ id }) => ({
                type: "Campaign",
                id,
              })),
              { type: "Campaign", id: "LIST" },
            ]
          : [{ type: "Campaign", id: "LIST" }],
    }),

    // Get campaign list specific to tenant with pagination
    getCampaignsByTenant: builder.query({
      query: (params = {}) => {
        // Clean and prepare query parameters
        const queryParams = {
          page: typeof params === 'number' ? params : (params.page || 0),
          pageSize: params.pageSize || 8,
          ...(params.keyword && params.keyword.trim() && { keyword: params.keyword.trim() }),
          ...(params.status !== undefined && { status: params.status }),
          ...(params.categoryId && { categoryId: params.categoryId }),
          ...(params.brandId && { brandId: params.brandId }),
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
          // Only include sort parameters if they differ from server defaults
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }) };

        console.log('🔍 Campaign API Query Params (keyword):', queryParams);

        return {
          url: "/campaign/list",
          method: "GET",
          params: queryParams
        };
      },
      transformResponse: (response) => {
        if (!response) {
          return {
            value: {
              campaigns: [],
              totalCount: 0,
            },
            isSuccess: false,
            errors: [],
            validationErrors: [],
          };
        }

        return {
          value: {
            campaigns: response.value.campaigns || [],
            totalCount: response.value.totalCount || 0,
          },
          isSuccess: response.isSuccess,
          errors: response.errors || [],
          validationErrors: response.validationErrors || [],
        };
      },
      providesTags: (result) =>
        result?.value?.campaigns
          ? [
              ...result.value.campaigns.map(({ id }) => ({
                type: "Campaign",
                id,
              })),
              { type: "Campaign", id: "TENANT_LIST" },
            ]
          : [{ type: "Campaign", id: "TENANT_LIST" }],
    }),

    // Get single campaign details
    getCampaignById: builder.query({
      query: (campaignId) => ({
        url: `/campaign/${campaignId}`,
        method: "GET",
      }),
      transformResponse: (response) => response.value,
      providesTags: (result, error, id) => [{ type: "Campaign", id }],
    }),

    // Create campaign
    createCampaign: builder.mutation({
      query: (data) => ({
        url: "/campaign/create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Campaign", id: "LIST" }],
    }),

    // Create tenant-specific campaign
    createTenantCampaign: builder.mutation({
      query: (data) => ({
        url: "/campaign/create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        { type: "Campaign", id: "LIST" },
        { type: "Campaign", id: "TENANT_LIST" },
      ],
    }),

    // Update campaign
    updateCampaign: builder.mutation({
      query: (data) => {
        console.log('🔥 Campaign Update API - Request Data:', data);
        return {
          url: "/campaign/update",
          method: "PUT",
          body: data,
        };
      },
      transformResponse: (response, meta) => {
        console.log('✅ Campaign Update API Response:', response);
        console.log('📡 Campaign Update Meta:', meta);
        
        // Handle empty response (204 No Content)
        if (meta?.response?.status === 204) {
          return { 
            isSuccess: true, 
            message: 'Kampanya başarıyla güncellendi',
            value: true 
          };
        }
        
        // Handle null response
        if (!response) {
          return { 
            isSuccess: true, 
            message: 'Kampanya başarıyla güncellendi',
            value: true 
          };
        }
        
        return response;
      },
      transformErrorResponse: (response) => {
        console.error('❌ Campaign Update API Error:', response);
        return {
          status: response.status,
          message: response.data?.message || 'Kampanya güncellenirken hata oluştu',
          errors: response.data?.errors || [],
          validationErrors: response.data?.validationErrors || []
        };
      },
      invalidatesTags: (result, error, { campaignId }) => [
        { type: "Campaign", id: campaignId },
        { type: "Campaign", id: "LIST" },
        { type: "Campaign", id: "TENANT_LIST" },
      ],
    }),

    // Delete campaign
    deleteCampaign: builder.mutation({
      query: (campaignId) => ({
        url: `/campaign/delete/${campaignId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Campaign", id },
        { type: "Campaign", id: "LIST" },
      ],
    }),

    // Add campaign media
    addCampaignMedia: builder.mutation({
      query: ({ file, orderNo }) => {
        console.log('🔥 Campaign Media API - Building Request');
        console.log('📁 API File Info:', {
          name: file?.name,
          size: file?.size,
          type: file?.type,
          orderNo: orderNo
        });

        const formData = new FormData();
        formData.append("file", file);
        formData.append("orderNo", orderNo.toString());

        console.log('📡 API Request Details:', {
          url: "/campaign/media",
          method: "POST",
          hasFile: !!file,
          orderNo: orderNo.toString()
        });

        return {
          url: "/campaign/media",
          method: "POST",
          body: formData,
          formData: true,
        };
      },
      transformResponse: (response) => {
        console.log('✅ Campaign Media API Response:', response);
        console.log('📡 API Response Details:', {
          isSuccess: response?.isSuccess,
          value: response?.value,
          errors: response?.errors,
          validationErrors: response?.validationErrors,
          status: response?.status
        });
        
        // 🎯 EXTRACT URL FROM RESPONSE AND NORMALIZE FORMAT
        let extractedUrl = null;
        
        // Try different response patterns
        if (response?.value?.url) {
          extractedUrl = response.value.url;
        } else if (response?.value && typeof response.value === 'string') {
          extractedUrl = response.value;
        } else if (response?.url) {
          extractedUrl = response.url;
        } else if (response?.data?.url) {
          extractedUrl = response.data.url;
        }
        
        console.log('🔍 Campaign Media URL Extraction:', {
          extractedUrl: extractedUrl,
          patterns: {
            'response.value.url': response?.value?.url,
            'response.value': response?.value,
            'response.url': response?.url,
            'response.data.url': response?.data?.url
          }
        });
        
        // 🎯 VALIDATE SUCCESS RESPONSE HAS URL
        if (response?.isSuccess && !extractedUrl) {
          console.error('❌ Campaign Media API - Successful response missing URL:', {
            response: response,
            extractedUrl: extractedUrl
          });
          throw new Error('Upload successful but missing URL in response');
        }
        
        // 🎯 RETURN NORMALIZED RESPONSE WITH URL
        return {
          ...response,
          url: extractedUrl,
          isSuccess: response?.isSuccess || false,
          errors: response?.errors || [],
          validationErrors: response?.validationErrors || []
        };
      },
      transformErrorResponse: (response) => {
        console.error('❌ Campaign Media API Error Response:', response);
        console.error('📡 API Error Details:', {
          status: response?.status,
          data: response?.data,
          message: response?.data?.message,
          errors: response?.data?.errors
        });
        return response;
      },
      invalidatesTags: ["Campaign"],
    }),

    // Get coupon statistics for a campaign
    getCouponStats: builder.query({
      query: (campaignId) => ({
        url: `/campaign/${campaignId}/coupons/stats`,
        method: "GET",
      }),
      transformResponse: (response) => {
        if (!response || !response.isSuccess) {
          return null;
        }
        return response.value;
      },
      providesTags: (result, error, campaignId) => [
        { type: "Campaign", id: `${campaignId}-stats` }
      ],
    }),

    // Add coupon codes to a campaign (incremental)
    addCouponCodes: builder.mutation({
      query: ({ campaignId, codesText }) => ({
        url: `/campaign/${campaignId}/coupons/add`,
        method: "POST",
        body: { codesText },
      }),
      invalidatesTags: (result, error, { campaignId }) => [
        { type: "CouponCodes", id: campaignId },
        { type: "Campaign", id: `${campaignId}-stats` },
      ],
    }),

    // List coupon codes for a campaign with pagination
    listCouponCodes: builder.query({
      query: ({ campaignId, page = 0, pageSize = 10, filter = 0, search, sortBy, sortOrder }) => ({
        url: `/campaign/${campaignId}/coupons/list`,
        method: "GET",
        params: {
          page,
          pageSize,
          filter,
          ...(search && { search }),
          ...(sortBy && { sortBy }),
          ...(sortOrder && { sortOrder }),
        },
      }),
      transformResponse: (response) => {
        if (!response || !response.isSuccess) {
          return { items: [], totalCount: 0, page: 0, pageSize: 10 };
        }
        return response.value;
      },
      providesTags: (result, error, { campaignId }) => [
        { type: "CouponCodes", id: campaignId },
      ],
    }),

    // Remove unused coupon codes from a campaign
    removeCouponCodes: builder.mutation({
      query: ({ campaignId, couponCodeIds }) => ({
        url: `/campaign/${campaignId}/coupons/remove`,
        method: "POST",
        body: { couponCodeIds },
      }),
      invalidatesTags: (result, error, { campaignId }) => [
        { type: "CouponCodes", id: campaignId },
        { type: "Campaign", id: `${campaignId}-stats` },
      ],
    }),

    // Get categories
    getCategories: builder.query({
      query: () => ({
        url: "/category/list",
        method: "GET",
      }),
      transformResponse: (response) => {
        if (!response) {
          return {
            value: [],
            isSuccess: false,
            errors: [],
            validationErrors: [],
          };
        }

        return {
          value: response.value || [],
          isSuccess: response.isSuccess,
          errors: response.errors || [],
          validationErrors: response.validationErrors || [],
        };
      },
      providesTags: ["Category"],
    }),

    // Get brands
    getBrands: builder.query({
      query: () => ({
        url: "/brand/list",
        method: "GET",
      }),
      transformResponse: (response) => response.value,
      providesTags: ["Brand"],
    }),
  }),
});

export const {
  useGetCampaignsQuery,
  useGetCampaignsByTenantQuery,
  useGetCampaignByIdQuery,
  useCreateCampaignMutation,
  useCreateTenantCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,
  useAddCampaignMediaMutation,
  useGetCouponStatsQuery,
  useAddCouponCodesMutation,
  useListCouponCodesQuery,
  useRemoveCouponCodesMutation,
  useGetCategoriesQuery,
  useGetBrandsQuery,
} = campaignApi;

export const {
  reducer: campaignReducer,
  middleware: campaignMiddleware,
  reducerPath: campaignReducerPath,
} = campaignApi;
